/**
 * Canonical AI agent capability keys stored in `ai_agents.allowedTools`.
 * Keep in sync with `functions/aiAgentCapabilities.js` tool ids.
 *
 * @typedef {{ id: string, label: string, description: string }} AiAgentCapabilityOption
 */

/** @type {AiAgentCapabilityOption[]} */
export const AI_AGENT_TOOL_OPTIONS = [
  {
    id: 'read_ticket_history',
    label: 'Read Ticket History',
    description: 'Reference and summarize prior ticket thread messages during runs.',
  },
  {
    id: 'write_code_snippets',
    label: 'Generate Code Snippets',
    description:
      'Edit existing files via patch_file find/replace (including large edits); apply_code_patch for create/delete only. List/revise pending Code Sandbox patches before resubmitting the same file. Primary Target Repository only. No direct GitHub PRs; no truncation placeholders.',
  },
  {
    id: 'read_repo_tree',
    label: 'Read Repository Tree',
    description:
      'Inspect GitHub layout for the primary Target Repository, or a ticket linkedReadRepos entry via optional repo=owner/repoName.',
  },
  {
    id: 'read_file',
    label: 'Read File Contents',
    description:
      'Inspect raw source on the primary Target Repository, or a ticket linkedReadRepos entry via optional repo=owner/repoName.',
  },
  {
    id: 'create_github_repo',
    label: 'Create GitHub Repository',
    description:
      'Create a new private GitHub repo for the ticket (same classic PAT path as Game Studio) when none is linked yet.',
  },
  {
    id: 'create_ticket',
    label: 'Delegate Work via Tickets',
    description:
      'Reassign the current ticket, create delegated sub-tickets (inheriting Target Repository + linkedReadRepos by default), or yield via update_ticket_status when waiting on other agents.',
  },
  {
    id: 'update_ticket_status',
    label: 'Update Ticket Status',
    description:
      'Update the current ticket status (e.g. Awaiting HQ Verification) without creating or reassigning tickets.',
  },
  {
    id: 'reply_to_ticket',
    label: 'Reply to Ticket',
    description:
      'Post clarifying replies and acknowledgements on the ticket thread for tenants or teammates.',
  },
  {
    id: 'analyze_schema',
    label: 'Propose Database Architecture',
    description: 'Recommend database or schema architecture changes.',
  },
  {
    id: 'request_human_review',
    label: 'Request Management Intervention',
    description: 'Escalate to a human when risk, ambiguity, or approval is required.',
  },
];

export const AI_AGENT_TOOL_IDS = AI_AGENT_TOOL_OPTIONS.map((o) => o.id);

export const DEFAULT_CONTEXT_MEMORY_LIMIT = 10;
export const MIN_CONTEXT_MEMORY_LIMIT = 1;
export const MAX_CONTEXT_MEMORY_LIMIT = 50;

/**
 * @param {string} toolId
 * @returns {string}
 */
export function aiAgentToolLabel(toolId) {
  const match = AI_AGENT_TOOL_OPTIONS.find((o) => o.id === toolId);
  return match?.label || toolId;
}

/**
 * @param {string} toolId
 * @returns {string}
 */
export function aiAgentToolDescription(toolId) {
  const match = AI_AGENT_TOOL_OPTIONS.find((o) => o.id === toolId);
  return match?.description || '';
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeAllowedTools(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((t) => typeof t === 'string' && AI_AGENT_TOOL_IDS.includes(t));
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizeContextMemoryLimit(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return DEFAULT_CONTEXT_MEMORY_LIMIT;
  return Math.min(MAX_CONTEXT_MEMORY_LIMIT, Math.max(MIN_CONTEXT_MEMORY_LIMIT, Math.round(n)));
}
